import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { usePatchUniversityMutation } from '@lok/services/university';
import { useAdmin } from '@lok/context/AdminContext';

const MIME_TO_EXT = {
  'image/png': 'png', 'image/jpeg': 'jpg', 'image/gif': 'gif',
  'image/webp': 'webp', 'image/svg+xml': 'svg', 'image/bmp': 'bmp',
  'image/tiff': 'tiff', 'image/x-icon': 'ico'
};

const urlToFile = async (url, fileName = 'logo.png') => {
  const response = await fetch(url);
  if (!response.ok) throw new Error('Failed to fetch image');
  const blob = await response.blob();
  const mimeType = blob.type || 'image/png';

  // Ensure fileName has a valid image extension
  const hasValidExt = /\.(png|jpe?g|gif|webp|svg|bmp|tiff?|ico)$/i.test(fileName);
  let finalFileName = fileName;

  if (!hasValidExt) {
    const ext = MIME_TO_EXT[mimeType] || 'png';
    finalFileName = `${finalFileName || 'logo'}.${ext}`;
  }

  return new File([blob], finalFileName, { type: mimeType });
};

const buildLogoFormData = (logoFile, logoDarkFile) => {
  const formData = new FormData();
  if (logoFile instanceof File) {
    formData.append('logo', logoFile);
  } else if (logoFile === '') {
    formData.append('logo', '');
  }
  if (logoDarkFile instanceof File) {
    formData.append('logo_theme_dark', logoDarkFile);
  } else if (logoDarkFile === '') {
    formData.append('logo_theme_dark', '');
  }
  return formData;
};

const LogoUploadModal = ({
  university,
  currentLogo,
  currentLogoDark,
  onClose,
  onSuccess
}) => {
  const [tab, setTab] = useState('light');
  const [urlInput, setUrlInput] = useState('');
  const [previewLogo, setPreviewLogo] = useState(currentLogo || null);
  const [previewLogoDark, setPreviewLogoDark] = useState(
    currentLogoDark || null
  );
  const [logoFile, setLogoFile] = useState(null);
  const [logoFileDark, setLogoFileDark] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isFetchingUrl, setIsFetchingUrl] = useState(false);

  const [patchUniversity] = usePatchUniversityMutation();
  const { pushUndo } = useAdmin();

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      if (tab === 'light') {
        setPreviewLogo(reader.result);
        setLogoFile(file);
      } else {
        setPreviewLogoDark(reader.result);
        setLogoFileDark(file);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleUrlAdd = async () => {
    const url = urlInput.trim();
    if (!url) return;
    setIsFetchingUrl(true);
    try {
      const filename = url.split('/').pop().split('?')[0] || 'logo.png';
      const file = await urlToFile(url, filename);
      if (tab === 'light') {
        setPreviewLogo(url);
        setLogoFile(file);
      } else {
        setPreviewLogoDark(url);
        setLogoFileDark(file);
      }
      setUrlInput('');
    } catch {
      toast.error('Could not load image from that URL. Try downloading and uploading the file directly.');
    } finally {
      setIsFetchingUrl(false);
    }
  };

  const saveLogo = async (logoFileData, logoDarkFileData) => {
    if (!university?.id) {
      toast.error('University information is missing');
      return false;
    }

    setIsUploading(true);
    try {
      const formData = buildLogoFormData(logoFileData, logoDarkFileData);
      const response = await patchUniversity({
        id: university.id,
        data: formData
      });

      if (response?.error) {
        toast.error('Failed to update logo');
        return false;
      }

      pushUndo({
        type: 'logo',
        previousValue: { logo: currentLogo, logoDark: currentLogoDark },
        revert: async () => {
          const [prevFile, prevFileDark] = await Promise.all([
            currentLogo ? urlToFile(currentLogo, 'logo.png').catch(() => '') : Promise.resolve(''),
            currentLogoDark ? urlToFile(currentLogoDark, 'logo_dark.png').catch(() => '') : Promise.resolve('')
          ]);
          await patchUniversity({
            id: university.id,
            data: buildLogoFormData(prevFile, prevFileDark)
          });
        }
      });

      return true;
    } catch {
      toast.error('Failed to update logo');
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    if (!logoFile && !logoFileDark) {
      toast.error('Please select an image or enter a URL');
      return;
    }
    const success = await saveLogo(logoFile, logoFileDark);
    if (success) {
      toast.success('Logo updated successfully');
      onSuccess?.();
      onClose();
    }
  };

  const handleRemove = async () => {
    const success = await saveLogo('', '');
    if (success) {
      toast.success('Logo removed');
      onSuccess?.();
      onClose();
    }
  };

  const currentPreview = tab === 'light' ? previewLogo : previewLogoDark;

  // Use React Portal to render modal at document root to escape any stacking contexts
  return createPortal(
    <div
      role='presentation'
      className='fixed inset-0 z-[9999] flex items-center justify-center bg-black/50'
      onClick={onClose}
    >
      <div
        role='dialog'
        aria-modal='true'
        aria-label='Upload University Logo'
        className='bg-white dark:bg-[#1D2838] rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden'
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className='flex items-center justify-between px-5 pt-5 pb-3'>
          <h2 className='text-sm font-bold text-[#1C252E] dark:text-white'>
            Upload University Logo
          </h2>
          <button
            type='button'
            onClick={onClose}
            aria-label='Close'
            className='text-gray-400 dark:text-gray-500 hover:text-[#1C252E] dark:hover:text-white transition-colors'
          >
            <svg
              width='18'
              height='18'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
            >
              <path d='M18 6L6 18M6 6l12 12' />
            </svg>
          </button>
        </div>

        {/* Light / Dark preview tabs */}
        <div className='flex gap-1 px-5 pb-3'>
          {['light', 'dark'].map((mode) => (
            <button
              key={mode}
              type='button'
              onClick={() => setTab(mode)}
              className={`px-3 py-0.5 rounded-md text-xs font-semibold transition-colors ${tab === mode
                ? 'bg-primary-green text-primary-green-fg'
                : 'bg-gray-100 dark:bg-[#2A3441] text-[#637381] dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-[#354550]'
                }`}
            >
              {mode.charAt(0).toUpperCase() + mode.slice(1)} Mode
            </button>
          ))}
        </div>

        <div className='px-5 pb-5'>
          {/* Logo preview */}
          <div
            className={`rounded-xl overflow-hidden h-[120px] mb-4 flex items-center justify-center ${tab === 'dark' ? 'bg-[#1D2838]' : 'bg-[#F3F4F6]'
              }`}
          >
            {currentPreview ? (
              <img
                src={currentPreview}
                alt='Logo preview'
                className='max-w-full max-h-full object-contain p-4'
              />
            ) : (
              <p
                className={`text-xs ${tab === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}
              >
                No logo
              </p>
            )}
          </div>

          {/* File upload */}
          <div className='mb-3'>
            <label className='block text-xs font-medium text-[#637381] dark:text-gray-400 mb-1'>
              Upload File
              <input
                type='file'
                accept='image/*'
                onChange={handleFileChange}
                className='block w-full text-xs text-[#637381] dark:text-gray-400 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-primary-green file:text-primary-green-fg hover:file:bg-primary-green/80 file:cursor-pointer mt-1'
              />
            </label>
          </div>

          {/* URL input */}
          <div className='mb-4'>
            <label className='block text-xs font-medium text-[#637381] dark:text-gray-400 mb-1'>
              Or enter image URL
              <div className='flex gap-2 mt-1'>
                <input
                  type='text'
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleUrlAdd();
                  }}
                  placeholder='https://...'
                  disabled={isFetchingUrl}
                  className='flex-1 text-xs px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-[#2A3441] text-[#1C252E] dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-green disabled:opacity-50'
                />
                <button
                  type='button'
                  onClick={handleUrlAdd}
                  disabled={isFetchingUrl}
                  className='px-3 py-1 rounded-lg bg-primary-green hover:bg-primary-green/80 text-primary-green-fg text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {isFetchingUrl ? 'Loading...' : 'Add'}
                </button>
              </div>
            </label>
          </div>

          {/* Actions */}
          <div className='flex gap-2'>
            <button
              type='button'
              onClick={handleSave}
              disabled={isUploading}
              className='flex-1 py-1.5 rounded-lg bg-primary-green hover:bg-primary-green/80 text-primary-green-fg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {isUploading ? 'Saving...' : 'Save'}
            </button>
            {(currentLogo || currentLogoDark) && (
              <button
                type='button'
                onClick={handleRemove}
                disabled={isUploading}
                className='px-3 py-1.5 rounded-lg bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 text-xs font-semibold transition-colors disabled:opacity-50'
              >
                Remove
              </button>
            )}
            <button
              type='button'
              onClick={onClose}
              className='px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-[#2A3441] hover:bg-gray-200 dark:hover:bg-[#354550] text-[#1C252E] dark:text-white text-xs font-medium transition-colors'
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LogoUploadModal;
